import { SitesRepository } from "@/repositories/SitesRepository";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { queryClient } from '@/lib/queryClient';
import { siteQueryKeys } from '@/hooks/useSiteQuery';
import { SubscriptionService } from '@/services/subscription-service';
import { subscriptionQueryKeys } from '@/hooks/useSubscriptionQuery';
import { builderStartQueryKey, getBuilderStart, type StartStatus } from '@/features/builder/api/startApi';
import { cardSiteChosen } from '@/features/builder/lib/cardSiteChoice';

export const Route = createFileRoute("/_protected")({
  beforeLoad: async ({ context, location }) => {
    if (!context.auth.session) {
      throw redirect({
        to: "/auth/login",
        replace: true,
        search: { returnTo: undefined },
      });
    }

    const userId = context.auth.user?.id;
    
    // Pre-fetch subscription data
    const subscriptionCached = queryClient.getQueryData(subscriptionQueryKeys.subscription(userId!));
    if (!subscriptionCached && userId) {
      const subscription = await SubscriptionService.getUserPlan(userId);
      queryClient.setQueryData(subscriptionQueryKeys.subscription(userId), subscription);
    }
    
    // Check cache first for site data
    const cachedData = queryClient.getQueryData(siteQueryKeys.site(userId!));
    let data = null;
    
    if (cachedData) {
      data = cachedData;
    } else {
      const result = await SitesRepository.getByUserId();
      if (!result.error && result.data) {
        // Prime the cache
        queryClient.setQueryData(siteQueryKeys.site(userId!), { data: result.data, error: null });
        data = result.data;
      } else if (result.error && result.error.message !== 'No site found.') {
        throw result.error;
      }
    }

    // An owner who has started a builder site gets it at "/", even alongside an older card site,
    // unless they chose their card site: `/?view=card`, remembered for the tab's session so the card
    // editor's own links back to "/" stay there (see cardSiteChoice). The published card site is
    // untouched either way.
    const cardView = (location.search as { view?: unknown }).view === 'card' || cardSiteChosen();
    if (data && location.pathname === '/' && !cardView) {
      let builder: StartStatus | null = null;
      try {
        builder = await getBuilderStart();
      } catch {
        builder = null;
      }
      queryClient.setQueryData(builderStartQueryKey, builder);
      if (builder?.siteId) throw redirect({ to: "/builder", replace: true });
    }

    if (!data) {
      // Owners without a card site (all new owners) start in the builder when it's switched on here,
      // including at /create-site, so nobody new ends up with a card site. If the builder can't be
      // reached, the existing card-site onboarding still works.
      let builder: StartStatus | null = null;
      try {
        builder = await getBuilderStart();
      } catch {
        builder = null;
      }
      if (builder?.siteId) throw redirect({ to: "/builder", replace: true });
      if (builder?.available) throw redirect({ to: '/start', replace: true });
      if (location.pathname !== '/create-site') throw redirect({ to: '/create-site' });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  return <Outlet />;
}
